import { useState, useEffect } from 'react';
import { getAppMasterInfos, getCompanyName } from '../services/companyConfigService';
import { useAuth } from '../context/AuthProvider';

interface CompanyConfigMap {
  [key: string]: string | number | boolean;
}

export const useCompanyConfig = (env: 'production' | 'preproduction' | 'development' = 'production') => {
  const [configs, setConfigs] = useState<CompanyConfigMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    // Attendre que l'auth soit chargée ET que l'utilisateur soit authentifié
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchConfigs = async () => {
      try {
        setLoading(true);
        const name = getCompanyName();
        setCompanyName(name);
        
        if (!name) {
          throw new Error('Nom de l\'entreprise non trouvé');
        }

        const data = await getAppMasterInfos(name, env);
        
        setConfigs(data);
        setError(null);
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Erreur inconnue');
        setError(errorObj);
        console.error('Erreur lors du chargement des configurations pg_one:', errorObj);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [env, isAuthenticated, isLoading]);

  const getConfig = (key: string, defaultValue?: any): any => {
    return configs[key] !== undefined ? configs[key] : defaultValue;
  };

  const isModuleActive = (moduleName: 'compte' | 'magasin' | 'offres' | 'factures'): boolean => {
    const key = `module.${moduleName}.active`;
    const value = configs[key];
    return value === 1 || value === '1' || value === true;
  };

  return {
    configs,
    companyName,
    loading,
    error,
    getConfig,
    isModuleActive,
  };
};
